export function MediamarktTermsEN() {
  return (
    <article>
      <h1>Terms &amp; Conditions</h1>

      <time dateTime="2024-09-01">September 2024</time>

      <div>
        <h3>1. General</h3>
        <p>
          Jotta AS ("Jottacloud") offers a cloud storage service ("Service")
          that allows you to store, back up, synchronize, and share files over
          the internet. These terms of service ("Agreement"), along with our
          Privacy Policy, constitute a legally binding agreement between
          Jottacloud and you as the user. References to "we," "our," and "us"
          refer to Jottacloud, while "you," "your," and "user" refer to the
          user. By using the Service, you agree to and are bound by the terms of
          this Agreement.
        </p>
        <h3>2. User Account</h3>
        <p>
          To use the Service, you must set up a Jottacloud account. The
          available subscription plans are described in Jottacloud's pricing and
          subscription overview. Generally, the subscription types include a
          free account, a personal account for one user, a personal account for
          multiple users, and a business account.
        </p>
        <h3>3. Free Account</h3>
        <p>
          A Free Account is provided at no cost with specified limitations on
          storage capacity and features. Jottacloud also reserves the right to
          apply other restrictions and display paid advertisements on Free
          Accounts. A Free Account may be terminated by Jottacloud if it has
          been inactive for a period of one year or longer.
        </p>
        <h3>4. Personal Account with One or More Users</h3>
        <p>
          A Personal Account with one or more users is available exclusively for
          individual private use and not for businesses, organizations, or
          similar entities. A Personal Account with one or more users cannot be
          used to store data in a commercial context and comes with storage
          capacity and features according to the chosen subscription plan.
          Personal Accounts include both single-user subscriptions and shareable
          subscriptions for up to five users.
        </p>
        <p>
          A Personal Account with one or more users has a duration of one or
          twelve months and is then automatically renewed for a new subscription
          period (one or twelve months) unless the user cancels the paid
          subscription before the current subscription period ends. The user's
          credit card is automatically charged when a subscription is renewed.
          Payments are non-refundable, even if the user cancels. The user can
          cancel or change their subscription at any time. When a Personal
          Account with multiple users is established, an administrator is
          automatically created.
        </p>
        <p>
          The administrator is responsible for adding and maintaining the
          subscription and its members. The administrator identifies members of
          the subscription by providing email addresses or mobile numbers for
          each member.
        </p>
        <p>
          As an administrator, you are responsible for paying the fees for all
          members' use of the subscription. The administrator of a Personal
          Account with multiple users does not have access to data stored on
          members' accounts unless it is shared with the administrator. Members
          only have access to their own files unless other members have shared
          files with them. Members can share data and files with others.
        </p>
        <p>
          An administrator can upgrade (add storage space or members) or
          downgrade (remove storage space or reduce the number of members) the
          account or change the subscription period at any time. The
          administrator can cancel a Personal Account with multiple users at any
          time. Upon cancellation, all stored data will be deleted within 90
          days after the termination date.
        </p>
        <h3>5. Business Account</h3>
        <p>
          A company, organization, or individual can create a Business Account.
          When a Business Account is created, a Business Account administrator
          is automatically created. The Business Account administrator is
          responsible for adding and maintaining the Business Account and its
          members. Specific terms apply to Business Accounts, see section 6
          below.
        </p>
        <h3>6. Special Terms for Business Accounts</h3>
        <p>
          If you have a Business Account, this chapter (Special Terms for
          Business Accounts) applies to your use of the Service. A Business
          Account is set up by a Business Account administrator
          ("administrator") who chooses the type of business subscription and
          adds business account members ("members") to the account and creates
          individual accounts for each member.
        </p>
        <p>
          The administrator identifies members of the Business Account by
          providing email addresses for each member. As an administrator, you
          are responsible for paying the fees for all members' use of the
          Business Account. The administrator has exclusive control over the
          management of all member accounts and ensures that all use of the
          Service complies with this Agreement and other instructions from
          Jottacloud. It is the administrator's sole responsibility to ensure
          that storage capacity for the members is increased or reduced as
          needed. The administrator is also responsible for adding or removing
          individual accounts as needed. Bonus storage cannot be earned for
          Business Accounts.
        </p>
        <p>
          The administrator does not have access to data stored on members'
          accounts unless it is shared with the administrator. The administrator
          can read activity on members' accounts and thus read file and folder
          names with uploaded files and other account activity details. Members
          only have access to their own files unless other members have shared
          files with them. Members can share data and files with others. The
          business subscription is automatically renewed monthly or annually,
          depending on the administrator's choice. An administrator can upgrade
          (add storage space or the number of accounts) or downgrade (remove
          storage space or reduce the number of accounts) the account or change
          the subscription period at any time. Such changes will apply from the
          end of the current subscription period. The administrator can cancel a
          Business Account at any time. Upon cancellation, all stored data will
          be deleted.
        </p>
        <h3>7. License to Use the Service</h3>
        <p>
          When a user has signed up for an account in accordance with the
          license terms and Jottacloud's instructions, Jottacloud grants the
          user the right to use the Service.
        </p>
        <p>
          The user must only use the Service within the purpose and with the
          limitations and instructions provided by Jottacloud. Jottacloud grants
          you a non-exclusive, non-transferable license to install and use the
          Service for the specified purpose. The license cannot be transferred
          to others or otherwise made available to others beyond what is stated
          in this Agreement.
        </p>
        <p>
          Source code owned by third parties may be provided with the Service
          and is subject to the applicable third-party licenses. The Service and
          its structure, organization, source code, and documentation contain
          trade secrets.
        </p>
        <p>
          Jottacloud is the sole owner of the Service and the software used to
          deliver the Service, as well as all related documentation.
        </p>
        <p>
          The Service allows the user to share, send, and exchange digital
          content with others. The user must have all necessary rights to
          distribute this digital content. By using the Service, you acknowledge
          that Jottacloud has no control over the digital content sent through
          or used with the Service, and Jottacloud cannot be held responsible
          for the use, content, or storage of this digital content in any way.
        </p>
        <p>
          Jottacloud complies with applicable Norwegian law at all times and
          reserves the right to remove material or impose other restrictions
          deemed necessary to ensure compliance with applicable laws and
          regulations.
        </p>
        <p>
          Jottacloud reserves the right to conduct virus checks and may block
          certain files from being uploaded.
        </p>
        <p>
          The user cannot make commercial use of the Service, including, but not
          limited to, selling or distributing the Service, software, or
          documentation to a third party.
        </p>
        <h3>8. Upgrade and Downgrade</h3>
        <p>
          You can upgrade from a Free Account to a Personal Account or Home
          subscription by following Jottacloud's upgrade procedures. Similarly,
          you can downgrade from a Personal Account or Home subscription to a
          Free Account. Downgrading an account means changing from a Personal
          Account or Home subscription to a Free Account. Before you can
          complete such a downgrade, you must delete files from your Personal
          Account or Home subscription so that the total amount of data does not
          exceed the current storage limit for a Free Account. For Family
          Accounts, you must also remove all other members before downgrading to
          an Individual or Free Account.
        </p>
        <h3>9. Renewals, Termination, and Subscription Cancellation</h3>
        <p>
          Subscriptions for Personal Accounts, Home subscriptions, and Business
          Accounts are automatically renewed when a subscription period expires.
          The user can terminate the subscription at any time. When a
          subscription is terminated, it means that it will not be renewed when
          the subscription period ends. After a subscription is terminated,
          Jottacloud will either terminate the user's account or downgrade the
          user's account to a Free Account. If the account contains more than
          the current storage limit for a Free Account, all files in the account
          will be deleted during this downgrade. You can delete your user
          account at any time. This can be done under Settings, on the "Account
          and Profile" page. If the user fails to pay fees or other amounts owed
          to Jottacloud, Jottacloud has the right to terminate the account 90
          days after the original payment deadline. A subscription terminated on
          this basis may be terminated by Jottacloud after a notice period,
          which means that files and data will be deleted. Jottacloud has the
          right to terminate the user's account if the user acts in a manner
          that is not in accordance with this Agreement or instructions given by
          Jottacloud. We will delete user data within 90 days after the user
          account has been terminated.
        </p>
        <h3>10. Age Requirement</h3>
        <p>
          The minimum age to create a Free Account, Individual, or Family
          Account is 13 years. Jottacloud has the right to delete all
          information, including the user account, data, and files if it turns
          out that the user who created the account is under 13 years old. For
          Family Accounts, the Family administrator must be over 13 years old.
        </p>
        <h3>11. Changes in Personal Information</h3>
        <p>
          The user must notify Jottacloud of changes to their email address and
          payment information immediately by updating the account settings under
          Settings. The user is solely responsible for any costs that may arise
          from incorrect or missing updates to user information.
        </p>
        <h3>12. Privacy and Data Collection</h3>
        <p>
          Jottacloud may collect non-personally identifiable information related
          to the use of the Service. This includes statistics and performance
          measurements related to the software. This information is sent to
          Jottacloud and may only be used by Jottacloud as set forth in
          Jottacloud’s Privacy Policy. If there is a valid court order from a
          Norwegian court, Jottacloud will release data in accordance with the
          court order. Jottacloud will notify the user in such cases if it is
          legally permissible to do so.
        </p>
        <h3>13. Bonus Storage</h3>
        <p>
          You can earn additional free storage space ("Bonus Storage") on your
          account. Bonus Storage is free storage space made available on your
          user account for a limited period. Bonus Storage is only available for
          Free accounts and Individual accounts. Jottacloud reserves the right
          to cancel or change the terms of Bonus Storage.
        </p>
        <h3>14. Requirements to Use the Service</h3>
        <p>
          To use the Service, you must have a compatible computer and/or mobile
          device with Internet access. If you use the Service via a wireless
          connection, it may incur additional costs from your mobile operator.
          You are responsible for paying all data costs and for all equipment
          and other third-party services you use to access the Service.
        </p>
        <p>
          The user is responsible for remembering their password and keeping it
          confidential. The user is responsible for any activity that occurs
          under their username. If the user loses the password for their
          account, they may risk not being able to access their data. The user's
          username and password are personal and cannot be shared with a third
          party. If the user becomes aware of misuse of their account, they must
          notify Jottacloud immediately.
        </p>
        <h3>15. Fair and Acceptable Use</h3>
        <p>
          All use of Jottacloud's products and services must comply with the
          principles described in this Agreement and applicable laws and
          regulations. Jottacloud may establish or expand operational routines
          to ensure the availability of the Service and to prevent misuse.
        </p>
        <p>
          As part of these operational routines, we reserve the right to monitor
          our systems to identify excessive use of storage and network resources
          and to implement technical and other measures as we deem necessary.
          Restrictions on bandwidth and the number of API calls may be
          implemented.
        </p>
        <p>
          The Jottacloud Personal subscription with unlimited storage is
          designed to provide the customer with predictability. Unlimited refers
          to the available storage space. If the total storage amount exceeds 5
          TB on a Personal unlimited subscription, the upload speed will be
          gradually reduced.
        </p>
        <p>
          Users with a Personal account or Home subscription may use the Service
          for normal non-commercial use. We reserve the right to prevent
          excessive use and misuse of the Service.
        </p>
        <p>
          If a User's total storage and network consumption is significantly
          greater than the normal consumption of an average Jottacloud user,
          and/or indicates that the Service is used for something other than
          personal and non-commercial use, this may be considered misuse of the
          service.
        </p>
        <p>
          In the cases mentioned above, we reserve the right to offer an
          alternative Jotta subscription, terminate the customer relationship,
          or delete the User's account and/or user data. The user will be
          contacted by us with a proposal for change or notice of action in such
          cases.
        </p>
        <p>
          Users may not resell accounts or any of the account's services. You
          may not share your password with a third party and must store it in a
          secure and suitable manner. It is a violation of this Agreement to use
          Jottacloud's services and/or products for illegal activities.
        </p>
        <p>
          You may not store and/or distribute data on the Service that violates
          Norwegian laws and regulations, including privacy and copyright laws.
          This includes child pornography, pirated material, and/or other
          illegal or copyright-protected material.
        </p>
        <p>
          User accounts used to store and/or share illegal or
          copyright-protected material on the Service will be terminated.
          Jottacloud reserves the right to block access or prohibit the use of
          the Service, delete user data, and/or delete user accounts if we deem
          that the user is violating this Agreement.
        </p>
        <p>
          Storage and/or distribution of data that violates Norwegian law may be
          reported to the authorities.
        </p>
        <h3>16. Liability and Warranties</h3>
        <p>
          Jottacloud's total liability for any form of damage, cost, or loss
          related to the Service, including loss of data, is in any case limited
          to an amount equivalent to the paid fee for the relevant account in
          the last 12 months before the claim arose. Jottacloud is not liable
          for any form of indirect or consequential loss. These limitations are
          agreed upon by the parties and shall apply unless otherwise required
          by binding legal provisions
        </p>
        <h3>17. Marketing and Communication in Electronic Channels</h3>
        <p>
          Jottacloud will only send the user relevant and customized information
          about news, products, and services from the company in channels such
          as email and SMS if the user has given consent. The user can withdraw
          such consent at any time.
        </p>
        <h3>18. Changes to the Service and Terms of Use</h3>
        <p>
          Jottacloud reserves the right to change the terms and conditions of
          the Agreement with 30 (thirty) days' notice. Jottacloud also reserves
          the right to discontinue, change, or terminate the Service, in whole
          or in part, after notifying the user via email at least 30 (thirty)
          days prior to such change or termination of the Service. Jottacloud
          reserves the right to change the prices of ongoing subscriptions. The
          changes will take effect 30 (thirty) days after the notification is
          sent. All notices of changes mentioned above will be sent to the user
          via email.
        </p>
        <h3>19. Right to Cancel</h3>
        <p>
          The user has a period of 14 (fourteen) days after purchasing the
          Service to cancel the Service without any further obligations for the
          user. The right to cancel is only available for Personal accounts.
        </p>
        <h3>20. Miscellaneous</h3>
        <p>
          Jottacloud reserves the right to transfer this Agreement to a third
          party. Jottacloud will notify users of such a change. The user will be
          notified 3 (three) days in advance via email.
        </p>
        <h3>21. Governing Law and Dispute Resolution</h3>
        <p>
          This Agreement is governed by and interpreted in accordance with
          Norwegian law. The parties agree to Oslo District Court as the proper
          venue.
        </p>
        <h3>22. Customer Service</h3>
        <p>
          For questions about the Service, contact customer service via email at
          support@jottacloud.com. Jottacloud will attempt to respond to all
          inquiries within a reasonable timeframe.
        </p>
        <h3>23. Contact Information Email:</h3>
        <p>support@jottacloud.com, Jotta AS Øvre Slottsgate 5</p>
      </div>
    </article>
  )
}
